import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon, ArrowTopRightOnSquareIcon, ArrowLeftIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import {
  EdiAttachCandidate,
  EdiAttachPreview,
  getEdiAttachCandidates,
  getEdiAttachPreview,
} from '../services/pendingEdiOrders.service';
import { openDupeProSearch } from '../utils/ediDupes';

// =============================================================================
// EdiAttachModal
// =============================================================================
// The Attach-to-existing-pro dialog (V1 edi_attachpro), in two steps:
//   1. SELECT — the pros that match this pending EDI order (same dupe logic as the board's
//      dupeprolist, via app/services/edi_dupe.py), OR a free-entry pronumber of the user's
//      choice.
//   2. PREVIEW — a dry-run of the attach (app/services/edi_attach.py): the fields that WILL
//      change on the pro and the fields that differ but WON'T. A pro that isn't in the dupe
//      list shows the "does not match the edi info" warning as a banner here.
// The actual attach-save is a SEPARATE later step: Confirm hands the chosen pronumber back
// via onChosen. Portaled at z-[130] so it sits above the drill drawer.

interface Props {
  recno: number;
  title: string;                         // e.g. "FLEXPORT — flexportedi · order #2285833"
  onClose: () => void;
  /** Fired on Confirm with the chosen pro and any extra fields the user opted to overwrite
   *  (beyond the always-applied defaults). */
  onChosen: (pronumber: string, overrideFields: string[]) => void;
}

const norm = (v: string) => v.trim().toUpperCase();

const EdiAttachModal: React.FC<Props> = ({ recno, title, onClose, onChosen }) => {
  const [rows, setRows] = useState<EdiAttachCandidate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [manual, setManual] = useState('');

  // The pro staged for the preview step: its number + whether it was off the dupe list.
  const [stage, setStage] = useState<{ pro: string; offList: boolean } | null>(null);
  const [preview, setPreview] = useState<EdiAttachPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  // Fields the user opted to overwrite (beyond the locked defaults). Reset per staged pro.
  const [overrides, setOverrides] = useState<Set<string>>(new Set());
  const toggleOverride = (f: string) =>
    setOverrides((prev) => { const n = new Set(prev); if (n.has(f)) n.delete(f); else n.add(f); return n; });

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    getEdiAttachCandidates(recno)
      .then((resp) => { if (alive) setRows(resp.data); })
      .catch((e) => { if (alive) setError(e?.response?.data?.detail || e?.message || 'Failed to load matching pros.'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [recno]);

  // Fetch the preview whenever a pro is staged.
  useEffect(() => {
    setOverrides(new Set());  // a fresh pro starts with only the locked defaults
    if (!stage) { setPreview(null); setPreviewError(null); return; }
    let alive = true;
    setPreviewLoading(true);
    setPreviewError(null);
    getEdiAttachPreview(recno, stage.pro)
      .then((p) => { if (alive) setPreview(p); })
      .catch((e) => { if (alive) setPreviewError(e?.response?.data?.detail || e?.message || 'Failed to load the preview.'); })
      .finally(() => { if (alive) setPreviewLoading(false); });
    return () => { alive = false; };
  }, [stage, recno]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (stage) setStage(null); else onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, stage]);

  const candidateSet = useMemo(() => new Set(rows.map((r) => norm(String(r.pronumber)))), [rows]);

  const stagePro = (pro: string) => {
    const p = norm(pro);
    if (!p) return;
    setStage({ pro: p, offList: !candidateSet.has(p) });
  };

  const TH = 'px-2 py-1.5 text-left text-[0.6875rem] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-300 whitespace-nowrap';
  const TD = 'px-2 py-1.5 align-top text-slate-700 dark:text-gray-200';

  // ---- Preview (step 2) ----
  const renderPreview = () => {
    const p = preview;
    const will = p?.will_update || [];
    const wont = p?.wont_update || [];
    const wontRemaining = wont.filter((f) => !overrides.has(f.field));
    return (
      <>
        <div className="flex-1 min-h-0 overflow-auto p-3 space-y-4">
          {stage?.offList && (
            <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-900/30 px-3 py-2 text-sm text-amber-800 dark:text-amber-200">
              <ExclamationTriangleIcon className="w-5 h-5 shrink-0" />
              <span>Pronumber {stage.pro} does not match the edi info. Review the changes below before attaching.</span>
            </div>
          )}
          {previewError && (
            <div className="rounded-md border border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/30 px-3 py-2 text-sm text-red-800 dark:text-red-300">
              {previewError}
            </div>
          )}
          {previewLoading ? (
            <div className="p-8 text-center text-sm text-muted">Loading preview…</div>
          ) : p && !p.found ? (
            <div className="rounded-md border border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/30 px-3 py-3 text-sm text-red-800 dark:text-red-300">
              Pro {stage?.pro} was not found. Enter a valid pronumber.
            </div>
          ) : p ? (
            <>
              <section>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-green-700 dark:text-green-400 mb-1">
                  Will be updated ({will.length})
                </h3>
                {will.length === 0 && overrides.size === 0 ? (
                  <p className="text-sm text-slate-500 dark:text-gray-400">No fields will change on this pro.</p>
                ) : (
                  <table className="w-full text-xs border-collapse">
                    <thead className="bg-slate-100 dark:bg-slate-800">
                      <tr><th className={`${TH} w-8`} title="Always applied — can't be unchecked"></th><th className={TH}>Field</th><th className={TH}>Pro (now)</th><th className={TH}>EDI (new)</th><th className={TH}>Why</th></tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
                      {will.map((f) => (
                        <tr key={f.field}>
                          <td className={`${TD} text-center`}>
                            <input type="checkbox" checked disabled className="accent-green-600" title="Always applied" />
                          </td>
                          <td className={`${TD} font-medium`}>{f.label}</td>
                          <td className={`${TD} text-slate-400 line-through`}>{f.from || <span className="italic">blank</span>}</td>
                          <td className={`${TD} font-medium text-green-700 dark:text-green-400`}>{f.to}</td>
                          <td className={`${TD} text-slate-500 dark:text-gray-400`}>{f.reason}</td>
                        </tr>
                      ))}
                      {/* User-opted overrides (from the section below) also apply on attach. */}
                      {wont.filter((f) => overrides.has(f.field)).map((f) => (
                        <tr key={`ovr-${f.field}`} className="bg-green-50/60 dark:bg-green-900/10">
                          <td className={`${TD} text-center`}>
                            <input type="checkbox" checked onChange={() => toggleOverride(f.field)}
                              className="accent-green-600" title="Overwrite chosen — uncheck to keep the pro value" />
                          </td>
                          <td className={`${TD} font-medium`}>{f.label}</td>
                          <td className={`${TD} text-slate-400 line-through`}>{f.pro || <span className="italic">blank</span>}</td>
                          <td className={`${TD} font-medium text-green-700 dark:text-green-400`}>{f.edi}</td>
                          <td className={`${TD} text-slate-500 dark:text-gray-400`}>you chose to overwrite</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </section>

              {wontRemaining.length > 0 && (
                <section>
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-400 mb-1">
                    Differs but will NOT change ({wontRemaining.length})
                  </h3>
                  <p className="text-[0.6875rem] text-slate-500 dark:text-gray-400 mb-1">
                    The pro already has these values; the EDI values are ignored. Check <em>Overwrite</em> to apply the
                    EDI value anyway (Bill&nbsp;To, Customer and leg locations can't be overwritten on attach).
                  </p>
                  <table className="w-full text-xs border-collapse">
                    <thead className="bg-slate-100 dark:bg-slate-800">
                      <tr><th className={`${TH} w-20`}>Overwrite</th><th className={TH}>Field</th><th className={TH}>Pro (kept)</th><th className={TH}>EDI (ignored)</th></tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
                      {wontRemaining.map((f) => (
                        <tr key={f.field}>
                          <td className={`${TD} text-center`}>
                            {f.overridable ? (
                              <input type="checkbox" checked={false} onChange={() => toggleOverride(f.field)}
                                className="accent-green-600" title={`Overwrite ${f.label} with the EDI value`} />
                            ) : (
                              <span className="text-slate-300 dark:text-gray-600" title="Attach never changes this field">—</span>
                            )}
                          </td>
                          <td className={`${TD} font-medium`}>{f.label}</td>
                          <td className={`${TD} font-medium`}>{f.pro}</td>
                          <td className={`${TD} text-slate-500 dark:text-gray-400`}>{f.edi}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </section>
              )}
            </>
          ) : null}
        </div>

        <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-t border-slate-200 dark:border-gray-700">
          <button type="button" onClick={() => setStage(null)}
            className="inline-flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium text-label bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600">
            <ArrowLeftIcon className="w-4 h-4" /> Back
          </button>
          <button
            type="button"
            onClick={() => stage && onChosen(stage.pro, Array.from(overrides))}
            disabled={previewLoading || !!(preview && !preview.found)}
            className="px-5 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            Confirm Attach to {stage?.pro}{overrides.size > 0 ? ` (+${overrides.size} overwrite${overrides.size === 1 ? '' : 's'})` : ''}
          </button>
        </div>
      </>
    );
  };

  // ---- Select (step 1) ----
  const renderSelect = () => (
    <>
      <div className="flex-1 min-h-0 overflow-auto">
        {error && (
          <div className="m-3 rounded-md border border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/30 px-3 py-2 text-sm text-red-800 dark:text-red-300">
            {error}
          </div>
        )}
        {loading ? (
          <div className="p-8 text-center text-sm text-muted">Loading matching pros…</div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted">
            No pros match this EDI order. Enter a pronumber below to attach anyway.
          </div>
        ) : (
          <table className="w-full text-xs border-collapse">
            <thead className="sticky top-0 bg-slate-100 dark:bg-slate-800 z-10">
              <tr>
                <th className={TH}>Pronumber</th>
                <th className={TH}>Container</th>
                <th className={TH}>BOL</th>
                <th className={TH}>WO #</th>
                <th className={`${TH} text-right`}>EDI Files</th>
                <th className={TH}></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
              {rows.map((r) => (
                <tr key={r.pronumber} className="hover:bg-blue-50 dark:hover:bg-slate-800/60">
                  <td className={TD}>
                    <button
                      type="button"
                      onClick={() => stagePro(String(r.pronumber))}
                      className="inline-flex items-center rounded-md bg-blue-600 hover:bg-blue-700 px-2.5 py-1 text-xs font-medium text-white"
                      title={`Review attach to ${r.pronumber}`}
                    >
                      {r.pronumber}
                    </button>
                  </td>
                  <td className={`${TD}`}>{r.container}</td>
                  <td className={`${TD}`}>{r.bol}</td>
                  <td className={`${TD}`}>{r.billtorefno}</td>
                  <td className={`${TD} text-right tabular-nums`}>{r.numedifiles}</td>
                  <td className={TD}>
                    <button
                      type="button"
                      onClick={() => openDupeProSearch([String(r.pronumber)])}
                      className="inline-flex items-center gap-1 text-blue-600 dark:text-blue-400 hover:underline"
                      title="View this pro in a new tab"
                    >
                      <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" /> View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Free entry — attach to any pronumber (V1's "OR Lookup"). Off-list values are flagged
          on the preview step. */}
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-slate-200 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <label className="text-xs font-medium text-slate-600 dark:text-gray-300">Or attach to pronumber</label>
          <input
            type="text"
            value={manual}
            onChange={(e) => setManual(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') stagePro(manual); }}
            placeholder="Pronumber…"
            className="w-40 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white uppercase"
          />
          <button
            type="button"
            onClick={() => stagePro(manual)}
            disabled={!manual.trim()}
            className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            Review
          </button>
        </div>
        <button type="button" onClick={onClose}
          className="px-4 py-1.5 text-sm font-medium text-label bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600">
          Cancel
        </button>
      </div>
    </>
  );

  return createPortal(
    <div
      className="fixed inset-0 z-[130] bg-slate-900/40 flex items-center justify-center p-6"
      role="dialog"
      aria-label="Attach to Pronumber"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl border border-slate-200 dark:border-gray-700 w-full max-w-4xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-200 dark:border-gray-700">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-heading">
              {stage ? `Review attach → pro ${stage.pro}` : 'Attach to Pronumber'}
            </h2>
            <p className="text-xs text-slate-500 dark:text-gray-400 truncate">{title}</p>
          </div>
          <button type="button" onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200" aria-label="Close">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
        {stage ? renderPreview() : renderSelect()}
      </div>
    </div>,
    document.body,
  );
};

export default EdiAttachModal;
